import React, { useLayoutEffect } from 'react';
import { useSeoContext } from '../context/SeoContext';

// Helper function to find/create and set a meta tag
const setMetaTag = (selector: string, content: string, attribute: 'name' | 'property' = 'name') => {
  let el = document.head.querySelector(`meta[${attribute}="${selector}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attribute, selector);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
};

// Helper function to find/create and set a link tag
const setLinkTag = (rel: string, href: string) => {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', rel);
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

// Helper function to manage the JSON-LD script tag
const setScriptTag = (type: string, content: string) => {
  const SCRIPT_ID = 'seo-schema-script';
  let el = document.head.querySelector(`#${SCRIPT_ID}`);
  if (!el) {
    el = document.createElement('script');
    el.id = SCRIPT_ID;
    el.setAttribute('type', type);
    document.head.appendChild(el);
  }
  el.textContent = content;
};

const removeScriptTag = () => {
  const SCRIPT_ID = 'seo-schema-script';
  const el = document.head.querySelector(`#${SCRIPT_ID}`);
  if (el) {
    el.remove();
  }
};

const SeoManager: React.FC = () => {
  const { seoData } = useSeoContext();

  useLayoutEffect(() => {
    if (!seoData) return;

    const { title, description, canonicalUrl, ogType, ogImage, robots, schema } = seoData;

    // Update the document title directly
    document.title = title;

    // Update meta and link tags
    setMetaTag('description', description);
    setMetaTag('robots', robots);
    
    setMetaTag('og:type', ogType, 'property');
    setMetaTag('og:title', title, 'property');
    setMetaTag('og:description', description, 'property');
    setMetaTag('og:url', canonicalUrl, 'property');
    setMetaTag('og:image', ogImage, 'property');
    
    setMetaTag('twitter:title', title);
    setMetaTag('twitter:description', description);
    setMetaTag('twitter:image', ogImage);

    setLinkTag('canonical', canonicalUrl);
    
    // Update JSON-LD Schema
    if (schema) {
      setScriptTag('application/ld+json', JSON.stringify(schema, null, 2));
    } else {
      removeScriptTag();
    }

  }, [seoData]);

  // This component doesn't render anything itself
  return null;
};

export default SeoManager;